package com.exura.features.plandefinition.domain.builder.series

import com.exura.features.plandefinition.domain.builder.BuilderField
import com.exura.features.plandefinition.domain.model.element.InstructionElement
import com.exura.features.plandefinition.domain.model.series.HourOfDay
import com.exura.features.plandefinition.domain.model.series.PartOfDay
import com.exura.features.plandefinition.domain.model.series.SeriesSpawnType
import com.exura.features.plandefinition.domain.model.series.SimpleInstructionSeries

class SimpleInstructionSeriesBuilderNew : BaseAnchoredSeriesBuilder<InstructionElement, SimpleInstructionSeries>(
    SeriesSpawnType.INSTRUCTION
) {

    private val instruction = BuilderField<String>(isRequired = true)
    private val spawnEveryDays = BuilderField<Int>(isRequired = true, default = 1)
    private val spawnCount = BuilderField<Int?>()
    private val spawnForDays = BuilderField<Int?>()
    private val estimatedMinutes = BuilderField<Int>(isRequired = true, default = 5)
    private val partsOfDay = BuilderField<List<PartOfDay>?>()
    private val exactTimes = BuilderField<List<HourOfDay>?>()

    fun spawnForDays(): Int? = spawnForDays.getValue()
    fun spawnCount(): Int? = spawnCount.getValue()
    fun instruction(): String? = instruction.getValue()
    fun estimatedMinutes(): Int? = estimatedMinutes.getValue()
    fun partsOfDay(): List<PartOfDay>? = partsOfDay.getValue()
    fun exactTimes(): List<HourOfDay>? = exactTimes.getValue()
    fun spawnEveryDays(): Int? = spawnEveryDays.getValue()
    fun setSpawnForDays(newValue: Int?) = apply {
        val res = spawnForDays.setValue(newValue)
        updateChangedState(res)
        updateBuildState(res)
        updateElementsCalculationState(res)
    }
    fun setSpawnCount(newValue: Int?) = apply {
        val res = spawnCount.setValue(newValue)
        updateChangedState(res)
        updateBuildState(res)
        updateElementsCalculationState(res)
    }
    fun setInstruction(newValue: String) = apply {
        val res = instruction.setValue(newValue)
        updateChangedState(res)
        updateBuildState(res)
        updateElementsCalculationState(res)
    }
    fun setEstimatedMinutes(newValue: Int) = apply {
        val res = estimatedMinutes.setValue(newValue)
        updateChangedState(res)
        updateBuildState(res)
        updateElementsCalculationState(res)
    }
    fun setPartsOfDay(newValue: List<PartOfDay>?) = apply {
        val res = partsOfDay.setValue(newValue)
        updateChangedState(res)
        updateBuildState(res)
        updateElementsCalculationState(res)
    }
    fun setExactTimes(newValue: List<HourOfDay>?) = apply {
        val res = exactTimes.setValue(newValue)
        updateChangedState(res)
        updateBuildState(res)
        updateElementsCalculationState(res)
    }

    fun setSpawnEveryDays(newValue: Int) = apply {
        val res = spawnEveryDays.setValue(newValue)
        updateChangedState(res)
        updateBuildState(res)
        updateElementsCalculationState(res)
    }

    override fun markSaved() = apply {
        spawnForDays.markSaved()
        spawnCount.markSaved()
        instruction.markSaved()
        estimatedMinutes.markSaved()
        partsOfDay.markSaved()
        exactTimes.markSaved()
        spawnEveryDays.markSaved()
        super.markSaved()
    }

    override fun validate(): List<String> {
        val fields = mapOf(
            "instruction" to instruction,
            "spawnForDays" to spawnForDays,
            "spawnCount" to spawnCount,
            "estimatedMinutes" to estimatedMinutes,
            "partsOfDay" to partsOfDay,
            "exactTimes" to exactTimes,
            "spawnEveryDays" to spawnEveryDays,
        )
        val messages = validateFields(fields).toMutableList()
        val hasPartsOfDay = partsOfDay.getValue().isNullOrEmpty()
        val hasExactTimes = exactTimes.getValue().isNullOrEmpty()

        if (hasPartsOfDay == hasExactTimes) {
           messages.add("Either partsOfDay or exactTimes must be provided, but not both")
        }

        // Either/or validation for spawn duration
        val hasSpawnForDays = spawnForDays.getValue() != null
        val hasSpawnCount = spawnCount.getValue() != null

        if (hasSpawnForDays == hasSpawnCount) {
            messages.add("Exactly one of spawnForDays or spawnCount must be specified")
        }

        return messages + super.validate()
    }

    override fun build(): SimpleInstructionSeries {
        return SimpleInstructionSeries(
            firebaseDocId = firebaseDocId(),
            firebaseDocPrefix = null,
            title = name() ?: "",
            description = description() ?: "",
            relativeDay = relativeDay() ?: 0,
            spawnForDays = spawnForDays(),
            spawnCount = spawnCount(),
            instruction = instruction() ?: "",
            estimatedMinutes = estimatedMinutes() ?: 0,
            partsOfDay = partsOfDay(),
            exactTimes = exactTimes(),
            imageUrl = imageUrl(),
            spawnEveryDays = spawnEveryDays() ?: 1,
            elements = emptyList()
        )
    }

}

fun SimpleInstructionSeries.toBuilder(): SimpleInstructionSeriesBuilderNew {
    val series = this
    return SimpleInstructionSeriesBuilderNew().apply {
        setFirebaseDocId(series.firebaseDocId)
        setName(series.title)
        setDescription(series.description)
        setImageUrl(series.imageUrl)
        setRelativeDay(series.relativeDay)
        setSpawnForDays(series.spawnForDays)
        setSpawnCount(series.spawnCount)
        setInstruction(series.instruction)
        setEstimatedMinutes(series.estimatedMinutes)
        setPartsOfDay(series.partsOfDay)
        setExactTimes(series.exactTimes)
        setSpawnEveryDays(series.spawnEveryDays)
        markSaved()
    }
}
